import type { QueryClient } from "@tanstack/react-query";
import type { AdminView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { assertPrivateSession, getPrivateSessionState } from "@/lib/api/private-session";
import { signOutAdminFirebaseAuth } from "@/lib/firebase/auth-session";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

export type AdminSessionResponse = { admin?: AdminView; firebaseToken?: string | null };
const credentials = new WeakMap<QueryClient, { generation: number; scope: string; token: string; issuedAt: number }>();
const pending = new WeakSet<QueryClient>();
export const takeSessionCredentialRefresh = (client: QueryClient) => pending.delete(client);
const scopeOf = (payload: AdminSessionResponse) => JSON.stringify([payload.admin?.id, [...(payload.admin?.roles ?? [])].sort()]);

export async function readSession(client: QueryClient, signal: AbortSignal): Promise<AdminSessionResponse> {
  const generation = getPrivateSessionState().generation;
  const previous = client.getQueryData<AdminSessionResponse>(queryKeys.admin.session());
  const cached = credentials.get(client);
  const age = cached ? Date.now() - cached.issuedAt : Infinity;
  const reuse = cached && previous?.firebaseToken === cached.token && cached.generation === generation && cached.scope === scopeOf(previous) && age >= 0 && age < 50 * 60_000;
  let payload = await apiJsonClient<AdminSessionResponse>(`${endpoints.admin.auth.me}${reuse ? "" : "?includeFirebaseToken=true"}`, { signal, cache: "no-store" });
  assertPrivateSession(generation);
  if (signal.aborted) throw new DOMException("Session read aborted", "AbortError");
  const scope = scopeOf(payload);
  if (previous && scopeOf(previous) !== scope) void signOutAdminFirebaseAuth().catch(() => undefined);
  if (reuse && cached.scope === scope) return { ...payload, firebaseToken: cached.token };
  credentials.delete(client);
  if (reuse) { pending.add(client); payload = { ...payload, firebaseToken: null }; }
  if (payload.firebaseToken) credentials.set(client, { generation, scope, token: payload.firebaseToken, issuedAt: Date.now() });
  return payload;
}
